import { computed, ref } from 'vue'
import { useInstallPrompt } from '@/composables/useInstallPrompt'
import { isStandalone } from '@/composables/usePush'

// Install-nudgen: et diskret forslag om at laegge appen paa hjemmeskaermen — men foerst
// naar besoeget har bevist sig (3. besoeg), aldrig i den installerede app, og aldrig igen
// efter et afslag. En PWA man ikke har installeret, glemmer man; en banner man ikke kan
// slippe af med, sletter man.

const STORAGE_KEY = 'install-nudge'
const SESSION_KEY = 'install-nudge-visit'
const MIN_VISITS = 3

interface NudgeState {
  visits: number
  dismissed: boolean
}

function fraLager(): NudgeState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { visits: 0, dismissed: false, ...(JSON.parse(raw) as Partial<NudgeState>) }
  } catch {
    // Privat browsing kan afvise laesning — saa starter vi bare forfra.
  }
  return { visits: 0, dismissed: false }
}

function tilLager(state: NudgeState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Kan lageret ikke skrives, kan nudgen heller aldrig naa 3 besoeg — fint.
  }
}

const state = ref<NudgeState>(fraLager())

// Ét besoeg = én browsersession, talt ved modulets foerste import (app-start).
// I den installerede app taelles og vises intet — maalet er allerede naaet.
try {
  if (!isStandalone() && !sessionStorage.getItem(SESSION_KEY)) {
    sessionStorage.setItem(SESSION_KEY, '1')
    state.value = { ...state.value, visits: state.value.visits + 1 }
    tilLager(state.value)
  }
} catch {
  // Uden sessionStorage kan besoeg ikke taelles paalideligt — hellere ingen nudge end en ny pr. reload.
}

/** iOS Safari har ingen beforeinstallprompt — dér er vejen en anvisning, ikke en knap. */
export function isIos(): boolean {
  try {
    return /iphone|ipad|ipod/i.test(navigator.userAgent)
  } catch {
    return false
  }
}

export function useInstallNudge() {
  const { installable, promptInstall } = useInstallPrompt()

  const visible = computed(
    () =>
      !state.value.dismissed &&
      state.value.visits >= MIN_VISITS &&
      !isStandalone() &&
      // Kun hvor der findes en vej videre: nativ prompt (Chrome/Android) eller iOS-anvisningen.
      (installable.value || isIos()),
  )

  function dismiss(): void {
    state.value = { ...state.value, dismissed: true }
    tilLager(state.value)
  }

  async function install(): Promise<void> {
    await promptInstall()
    // Uanset svaret i den native prompt: spoerg aldrig igen herfra.
    dismiss()
  }

  return { visible, installable, dismiss, install }
}
